/**
 * Shared pipeline for external uploads.
 *
 * Entry points share the same middle (folder-conflict prompts, mkdir-p,
 * per-file conflict prompts):
 *   - collectDroppedFiles(dragEvent) → processCollectedDrop(snapshot) — for
 *     drag-drop. Collect walks the DataTransfer entry tree synchronously (so
 *     the entries survive any await before processing); process runs the rest.
 *   - processPickedFiles(fileList) — for `<input webkitdirectory>`. Files
 *     arrive flat with webkitRelativePath set by the browser.
 *
 * Used by DriveView, WorkspaceExplorer, and WorkspaceView for consistent UX.
 */
import { filesApi, type ConflictStrategy } from './index'
import type { ConflictResolution, ConflictOperation, ConflictResult, ConflictInfo } from '@papan/core-ui/ui'
import { isPinnedBatchIdentityLost, type PinnedBatch } from '@papan/api-client'

export interface FileToUpload {
  file: File
  parentId: string | null
  conflictStrategy?: ConflictStrategy
}

export interface DropUploadOptions {
  /** Target folder ID for uploaded files (null = root) */
  targetFolderId: string | null
  /** Conflict resolution callback (from useConflict()) */
  resolveConflict: (conflict: ConflictInfo) => Promise<ConflictResult>
  /** Called after file scanning completes with count of files found */
  onScanComplete?: (filesFound: number, hasDirectories: boolean) => void
  /** Called as each folder is created on the server */
  onFolderProgress?: (created: number, total: number) => void
  /** #1316 review round 2 (owner, 2026-10-28): the upload the person started (`pinnedBatch()`) — every request is sent
   *  as the identity that started it; once that session ended nothing is ASKED (a conflicting item is skipped). */
  batch?: PinnedBatch
}

export interface DropUploadResult {
  /** Files ready to upload with resolved conflicts and correct parent IDs */
  uploads: FileToUpload[]
  /** Whether any directories were dropped (folder structure was created) */
  createdFolders: boolean
}

/**
 * Read all files from a dropped directory, preserving relative paths.
 */
async function readDirectory(entry: FileSystemDirectoryEntry, path: string): Promise<File[]> {
  const result: File[] = []
  const reader = entry.createReader()
  const readEntries = (): Promise<FileSystemEntry[]> =>
    new Promise((resolve, reject) => reader.readEntries(resolve, reject))

  let entries: FileSystemEntry[] = []
  let batch: FileSystemEntry[]
  do {
    batch = await readEntries()
    entries = entries.concat(batch)
  } while (batch.length > 0)

  for (const e of entries) {
    if (e.isFile) {
      const file = await new Promise<File>((resolve, reject) =>
        (e as FileSystemFileEntry).file(resolve, reject))
      Object.defineProperty(file, 'webkitRelativePath', { value: path + '/' + file.name })
      result.push(file)
    } else if (e.isDirectory) {
      const subFiles = await readDirectory(e as FileSystemDirectoryEntry, path + '/' + e.name)
      result.push(...subFiles)
    }
  }
  return result
}

/**
 * Collect files from a DragEvent using the FileSystemEntry API.
 * Handles both individual files and recursive directory reading.
 */
export async function collectDroppedFiles(e: React.DragEvent): Promise<{ files: File[]; hasDirectories: boolean }> {
  const items = e.dataTransfer.items
  if (!items || items.length === 0) return { files: [], hasDirectories: false }

  // Must collect entries synchronously before DataTransferItemList is modified
  const entries: FileSystemEntry[] = []
  for (let i = 0; i < items.length; i++) {
    const entry = items[i].webkitGetAsEntry?.()
    if (entry) entries.push(entry)
  }

  const files: File[] = []
  const hasDirectories = entries.some(en => en.isDirectory)

  for (const entry of entries) {
    if (entry.isFile) {
      const file = await new Promise<File>((resolve, reject) =>
        (entry as FileSystemFileEntry).file(resolve, reject))
      files.push(file)
    } else if (entry.isDirectory) {
      const dirFiles = await readDirectory(entry as FileSystemDirectoryEntry, entry.name)
      files.push(...dirFiles)
    }
  }

  return { files, hasDirectories }
}

/**
 * Create folder structure on the server from dropped directory paths.
 * Returns a map from relative folder path to server folder ID.
 */
/** Resolution for a top-level folder conflict: merge, keepBoth, or skip */
type FolderResolution = 'merge' | 'keepBoth' | 'skip'

export async function createFolderStructure(
  files: File[],
  targetFolderId: string | null,
  onProgress?: (created: number, total: number) => void,
  /** Per top-level folder conflict resolution (from user prompt) */
  folderResolutions?: Map<string, FolderResolution>,
  /** #1316 review round 2: every folder is created as the identity that started the upload. */
  batch?: PinnedBatch,
): Promise<Map<string, string | null>> {
  const pin = () => batch?.opts() ?? {}
  const folderIdCache = new Map<string, string | null>()
  folderIdCache.set('', targetFolderId)

  const folderPaths = new Set<string>()
  for (const file of files) {
    const relPath = (file as File & { webkitRelativePath?: string }).webkitRelativePath || ''
    const parts = relPath.split('/')
    for (let i = 1; i < parts.length; i++) {
      folderPaths.add(parts.slice(0, i).join('/'))
    }
  }

  const sortedPaths = Array.from(folderPaths).sort()
  let created = 0
  for (const folderPath of sortedPaths) {
    const lastSlash = folderPath.lastIndexOf('/')
    const parentPath = lastSlash === -1 ? '' : folderPath.slice(0, lastSlash)
    const folderName = lastSlash === -1 ? folderPath : folderPath.slice(lastSlash + 1)
    const parentId = folderIdCache.get(parentPath) ?? targetFolderId

    // Determine resolution for this folder's top-level ancestor
    const topLevel = folderPath.split('/')[0]
    const resolution = folderResolutions?.get(topLevel) ?? 'merge'

    // Skip entire subtree if top-level folder was skipped
    if (resolution === 'skip') {
      created++
      onProgress?.(created, sortedPaths.length)
      continue
    }

    try {
      if (resolution === 'keepBoth' && folderPath === topLevel) {
        // Top-level "Keep Both" — server generates unique name (e.g. "reports (1)")
        const { folder } = await filesApi.createFolder(parentId, folderName, { keepBoth: true, ...pin() })
        folderIdCache.set(folderPath, folder.id)
      } else {
        // Merge: server returns existing folder if one with same name exists
        const { folder } = await filesApi.createFolder(parentId, folderName, { merge: true, ...pin() })
        folderIdCache.set(folderPath, folder.id)
      }
    } catch {
      // Fallback: look up existing folder directly
      try {
        const listing = await filesApi.listChildren(parentId, pin())
        const existing = listing.files.find(f => f.isFolder && f.name === folderName)
        if (existing) folderIdCache.set(folderPath, existing.id)
      } catch { /* fallback to targetFolderId */ }
    }
    created++
    onProgress?.(created, sortedPaths.length)
  }

  return folderIdCache
}

/**
 * Resolve the parent folder ID for a file based on its webkitRelativePath.
 */
function getParentId(
  file: File,
  folderIdCache: Map<string, string | null>,
  targetFolderId: string | null
): string | null {
  const relPath = (file as File & { webkitRelativePath?: string }).webkitRelativePath || ''
  if (!relPath.includes('/')) return targetFolderId
  const parentPath = relPath.slice(0, relPath.lastIndexOf('/'))
  return folderIdCache.get(parentPath) ?? targetFolderId
}

/**
 * Run the external-drop pipeline over an already-collected snapshot of dropped
 * files:
 * - Creating folder structure on the server for dropped directories
 * - Conflict detection and resolution via the provided callback
 *
 * Callers kick off collectDroppedFiles() synchronously inside the drop handler
 * — its webkitGetAsEntry() pass runs before the first await, so the entries are
 * captured before the browser detaches the DataTransfer — then `await` the
 * snapshot (possibly after other awaits, e.g. lazily creating a workspace) and
 * hand it here. Returns FileToUpload[] for the caller to upload with their
 * preferred method (grouped progress toast, simple loop, etc.)
 */
export async function processCollectedDrop(
  collected: Awaited<ReturnType<typeof collectDroppedFiles>>,
  options: DropUploadOptions,
): Promise<DropUploadResult | null> {
  const { files, hasDirectories } = collected
  if (files.length === 0) return null
  options.onScanComplete?.(files.length, hasDirectories)
  return processCollectedFiles(files, hasDirectories, options)
}

/**
 * Same pipeline as processExternalDrop but starting from a FileList
 * produced by `<input type="file" webkitdirectory multiple>`. Files
 * arrive flat with webkitRelativePath set by the browser.
 */
export async function processPickedFiles(
  fileList: FileList,
  options: DropUploadOptions,
): Promise<DropUploadResult | null> {
  if (fileList.length === 0) return null
  const files = Array.from(fileList)
  // A folder picker reports files with webkitRelativePath like
  // "myfolder/sub/file.txt". A plain file picker has empty paths.
  const hasDirectories = files.some(
    (f) => !!(f as File & { webkitRelativePath?: string }).webkitRelativePath?.includes('/'),
  )
  options.onScanComplete?.(files.length, hasDirectories)
  return processCollectedFiles(files, hasDirectories, options)
}

/**
 * Shared middle pipeline: folder-conflict prompts, mkdir-p, per-file
 * conflict prompts, build FileToUpload[]. Source-agnostic.
 */
async function processCollectedFiles(
  files: File[],
  hasDirectories: boolean,
  options: DropUploadOptions,
): Promise<DropUploadResult | null> {
  const { targetFolderId, resolveConflict, onFolderProgress, batch } = options
  const pin = () => batch?.opts() ?? {}
  let collectedFiles = files

  // 2. Check for top-level folder conflicts and prompt user (macOS-style: Merge / Keep Both / Skip)
  let folderResolutions: Map<string, FolderResolution> | undefined
  if (hasDirectories) {
    // Extract unique top-level folder names from dropped files
    const topLevelFolders = new Set<string>()
    for (const file of collectedFiles) {
      const relPath = (file as File & { webkitRelativePath?: string }).webkitRelativePath || ''
      const topLevel = relPath.split('/')[0]
      if (topLevel) topLevelFolders.add(topLevel)
    }

    // Check which top-level folders already exist in target
    const folderConflictCheck = await filesApi.checkConflicts(
      [...topLevelFolders].map(name => ({ name, parentId: targetFolderId })),
      pin(),
    )
    const conflicts = folderConflictCheck.map(c => c.name)

    // Prompt user for each conflicting folder
    if (conflicts.length > 0) {
      folderResolutions = new Map()
      let applyToAll: FolderResolution | null = null

      for (const folderName of conflicts) {
        // never a question in the session after the upload's own: the conflicting folder is skipped
        if (batch?.ended()) { folderResolutions.set(folderName, 'skip'); continue }
        if (applyToAll) {
          folderResolutions.set(folderName, applyToAll)
        } else {
          const result = await resolveConflict({
            fileName: folderName,
            sourcePath: folderName,
            destPath: folderName,
            isFolder: true,
            operation: 'upload' as ConflictOperation,
            showApplyToAll: conflicts.length > 1,
          })
          // Map conflict resolution to folder resolution:
          // replace (Merge, for a folder) → merge, keepBoth → keepBoth, skip → skip
          const mapped: FolderResolution =
            result.resolution === 'skip' ? 'skip' :
            result.resolution === 'keepBoth' ? 'keepBoth' : 'merge'
          folderResolutions.set(folderName, mapped)
          if (result.applyToAll) applyToAll = mapped
        }
      }
    }

    // Filter out files from skipped folders
    if (folderResolutions) {
      const skippedFolders = new Set(
        [...folderResolutions.entries()].filter(([, r]) => r === 'skip').map(([name]) => name)
      )
      if (skippedFolders.size > 0) {
        collectedFiles = collectedFiles.filter(f => {
          const relPath = (f as File & { webkitRelativePath?: string }).webkitRelativePath || ''
          const topLevel = relPath.split('/')[0]
          return !skippedFolders.has(topLevel)
        })
        if (collectedFiles.length === 0) return null
      }
    }
  }

  // 3. Create folder structure if directories were dropped
  let folderIdCache: Map<string, string | null>
  if (hasDirectories) {
    folderIdCache = await createFolderStructure(collectedFiles, targetFolderId, onFolderProgress, folderResolutions, batch)
  } else {
    folderIdCache = new Map<string, string | null>()
    folderIdCache.set('', targetFolderId)
  }

  // 4. Check for file conflicts (keyed by parentId+name to handle same-named files in different folders)
  const fileEntries = collectedFiles.map(f => ({
    file: f,
    parentId: getParentId(f, folderIdCache, targetFolderId),
    relPath: (f as File & { webkitRelativePath?: string }).webkitRelativePath || f.name,
  }))

  const conflicts = await filesApi.checkConflicts(
    fileEntries.map(e => ({ name: e.file.name, parentId: e.parentId })),
    pin(),
  )
  // The server resolves null parentIds to the tenant's root folder id
  // before responding, so a request like {parentId: null, name: 'foo'}
  // can come back as {parentId: 'papan_xyz', name: 'foo'}. Match the
  // response by name-only at the root tier (entries with null parentId)
  // and by parentId+name elsewhere — every entry under a real folder id
  // is unambiguous.
  const conflictKeysWithParent = new Set<string>()
  const conflictNamesAtRoot = new Set<string>()
  for (const c of conflicts) {
    conflictKeysWithParent.add(`${c.parentId ?? ''}:${c.name}`)
    conflictNamesAtRoot.add(c.name)
  }
  const isInConflict = (parentId: string | null, name: string) =>
    parentId === null
      ? conflictNamesAtRoot.has(name)
      : conflictKeysWithParent.has(`${parentId}:${name}`)

  // 5. Resolve conflicts interactively
  let applyToAll: ConflictResolution | null = null
  const resolutions = new Map<string, ConflictStrategy>() // keyed by "parentId:name"

  for (const entry of fileEntries) {
    const key = `${entry.parentId ?? ''}:${entry.file.name}`
    if (isInConflict(entry.parentId, entry.file.name)) {
      // never a question in the session after the upload's own: the conflicting file is skipped
      if (batch?.ended()) { resolutions.set(key, 'skip'); continue }
      if (applyToAll) {
        resolutions.set(key, applyToAll)
      } else {
        const result = await resolveConflict({
          fileName: entry.file.name,
          sourcePath: entry.relPath,
          destPath: entry.relPath,
          isFolder: false,
          operation: 'upload' as ConflictOperation,
        })
        resolutions.set(key, result.resolution)
        if (result.applyToAll) applyToAll = result.resolution
      }
    }
  }

  // 6. Build upload list
  const uploads: FileToUpload[] = fileEntries
    .filter(e => {
      const key = `${e.parentId ?? ''}:${e.file.name}`
      return resolutions.get(key) !== 'skip'
    })
    .map(e => {
      const key = `${e.parentId ?? ''}:${e.file.name}`
      return {
        file: e.file,
        parentId: e.parentId,
        conflictStrategy: resolutions.get(key),
      }
    })

  return { uploads, createdFolders: hasDirectories }
}

/** #1316 review round 2 (H3 0c): the direct upload paths (WorkspaceView's drop, App's drive upload) send each file as the
 *  batch's identity (`batch.opts()`, handed to `send`). A file whose identity was lost — the batch's session ended and
 *  its identity names no org email — is NOT sent and is counted in `lost`, so the caller says so like the upload dock
 *  does (`driveView.toast.uploadIdentityLost`); any other failure is counted in `failed`. */
export async function uploadFilesAsBatch<T, R>(
  batch: PinnedBatch,
  items: readonly T[],
  send: (item: T, opts: ReturnType<PinnedBatch['opts']>) => Promise<R>,
): Promise<{ done: R[]; failed: number; lost: number }> {
  const done: R[] = []
  let failed = 0
  let lost = 0
  for (const item of items) {
    try {
      done.push(await send(item, batch.opts()))
    } catch (err) {
      if (isPinnedBatchIdentityLost(err)) lost++
      else {
        failed++
        console.error('[upload] failed:', err)
      }
    }
  }
  return { done, failed, lost }
}
